import { getJson, loadScript, postJson, replaceState, URLX, useCurrentLanguage, useDynamicData, getDynamicData } from '@avensia/excite';
import * as React from 'react';
import { addCheckoutEvent } from './tracking/events/checkout';

interface AssetUrls {
  descriptive: string;
  standard: string;
}

interface PaymentMethodCategory {
  asset_urls: AssetUrls;
  identifier: string;
  name: string;
}

export interface KlarnaAuthorizeResponse {
  show_form: boolean;
  approved: boolean;
  finalize_required: boolean;
  client_token: string;
  session_id: string;
  payment_method_categories: PaymentMethodCategory[];
}

export function useKlarnaExpressCheckoutAuthorization(
  cart: Excite.Commercetools.CartApiModel | undefined,
  open: boolean,
) {
  const language = useCurrentLanguage();
  const [wellknownPages] = useDynamicData<NordicNest.Api.WellknownPagesApiModel>();
  const { expressCheckoutPageUrl } = wellknownPages ?? {};
  const { id, viewData } = cart ?? {};
  const { klarnaExpressActive } = viewData ?? {};
  const [authModel] = useDynamicData<NordicNest.Api.AuthenticationDetailsApiModel>();

  const clientId = authModel?.signInWithKlarnaScriptModel.clientId;
  React.useEffect(() => {
    if (open && id && expressCheckoutPageUrl && clientId && klarnaExpressActive) {
      loadScript('https://x.klarnacdn.net/kp/lib/v1/api.js').then(() => {
        window.Klarna.Payments.Buttons.init({
          client_id: clientId,
        }).load(
          {
            container: '#klarna-express-checkout',
            theme: 'outlined',
            shape: 'rect',
            locale: language,
            on_click: async (authorize: any) => {
              const url = new URLX('/api/klarnaexpresscheckout/orderpayload');
              url.searchParams.append('cartId', id);
              const { orderPayload } = await getJson<Excite.Klarna.Commercetools.OrderPayloadApiModel>(url);
              addCheckoutEvent();
              authorize(
                { auto_finalize: false, collect_shipping_address: true },
                orderPayload,
                async (result: KlarnaAuthorizeResponse) => {
                  if (result.approved) {
                    const expressCheckoutUrl = new URLX(expressCheckoutPageUrl);
                    expressCheckoutUrl.searchParams.append('client-token', result.client_token);
                    localStorage.setItem('klarna-express-checkout', JSON.stringify(result));
                    await postJson(new URLX('/api/klarnaexpresscheckout/addaddressoncart'), result);
                    getDynamicData<Excite.Commercetools.CheckoutApiModel>().refresh();
                    await replaceState(expressCheckoutUrl, { scrollPosition: 0 });
                  }
                },
              );
            },
          },
          function load_callback(loadResult: any) {
            // Here you can handle the result of loading the button
          },
        );
      });
    }
  }, [clientId, id, expressCheckoutPageUrl, language, open, klarnaExpressActive]);
}
